import React from 'react';

import { Flex, Box } from 'reflexbox';
import {
  Heading,
  Text,
} from 'rebass';
import PageHelmet from 'frontend/components/PageHelmet';

import { Link } from 'react-router';

const PrivacyEmailLink = () => (<a href="mailto:privacy@raidbots.com">privacy@raidbots.com</a>);
const RaidbotsAccountLink = () => (<Link to="/account" href="/account">Raidbots account page</Link>);

export const PrivacyPolicy = () => (
  <>
    <Heading level={1} mb={1}>Privacy Policy</Heading>
    <Text>
      This policy applies to all personal information collected, submitted, or otherwise processed on or through
      Raidbots&apos; website, Raidbots&apos; Discord community (including the Discord server and Discord bot), or via
      email communications with Raidbots LLC (&quot;Raidbots&quot;, &quot;we&quot;, &quot;our&quot;), and such other
      products and services as we may make available from time to time through any other devices and platforms
      (collectively, the &quot;Services&quot;).
    </Text>


    <Heading level={2} mb={1} mt={3}>The Short Version</Heading>
    <Text>
      Raidbots collects as little of your personal information (also referenced as &quot;personal data&quot; in this
      policy) as possible, uses that information to provide the services of the site, and only shares that information
      with third parties that assist in performing those services (e.g. sending notification emails that you request).
      We do not sell your information to third parties or share your personal information with third parties for
      commercial purposes (e.g.  selling lists of email addresses).
    </Text>


    <Heading level={2} mb={1} mt={3}>Personal Information We Collect</Heading>
    <Heading level={4} mb={1} mt={3}>Raidbots Website</Heading>

    <Text my={2}>
      To create an account on Raidbots, you must provide the following information:
    </Text>
    <ul>
      <li><Text>Email address</Text></li>
      <li><Text>Username</Text></li>
      <li><Text>Password</Text></li>
    </ul>
    <Text my={2}>
      This information is stored for as long as you have an account with Raidbots. Email addresses are used for logging
      in to the website, resetting your password, sending notifications that you request, and customer support services.
    </Text>
    <Text my={2}>
      We store the results of simulations you request and summaries of that information for up to 30 days (sim reports
      are stored for 28 days, archives generated from the &quot;Request Raw Data Archive&quot; tool are stored for up to
      2 days).
    </Text>
    <Text my={2}>
      If you have a user account for the Services, Raidbots uses both &quot;necessary&quot; cookies and
      &quot;preference&quot; cookies on the website to keep you logged in, and to provide you with the Services that you
      have requested. For example, Raidbots uses browser LocalStorage to keep track of various settings/preferences over
      time.
    </Text>
    <Text my={2}>
      Our servers store some technical information like IP addresses, browser, or other device information for a limited
      time as part of access logs - these are used to prevent fraud and abuse. In the future, we may use the IP address
      for geolocation to determine things such as default WoW region. Logs are retained for up to 3 months, and are
      subsequently deleted.
    </Text>

    <Heading level={4} mb={1} mt={3}>Discord and Discord Bot</Heading>
    <Text my={2}>
      If you connect your Raidbots account with your Discord account, we store the read-only token that allows us to
      link your accounts when using the Raidbots Discord bot. This token is used to handle requests to the Raidbots
      Discord bot and to apply any rewards (such as &quot;Skip-the-Line&quot; rewards) or preferences that you have set
      on your Raidbots account. This data is stored for as long your Raidbots account and Discord account are connected.
    </Text>
    <Text my={2}>
      The Raidbots Discord bot stores aggregate usage information (messages received, simulations run, etc). As with any
      Discord bot, all chat messages that are visible to the bot are inspected to determine if a user is sending it a
      command. When appropriate commands are issued to the bot, it will respond with appropriate functionality (running
      a simulation and reporting the results, setting a preference, etc).
    </Text>
    <Text my={2}>
      We may store anonymized statistics or messages for the purpose of improving the service.
    </Text>
    <Text my={2}>
      The Raidbots Discord Bot adheres to the
      {' '}
      <a
        href="https://discordapp.com/developers/docs/legal"
        target="_blank"
        rel="noopener noreferrer"
      >
        Discord Developer Terms of Service
      </a>.
    </Text>

    <Heading level={4} mb={1} mt={3}>Stripe / Raidbots Premium</Heading>
    <Text my={2}>
      Raidbots collects and stores your country, state/territory, and Postal/ZIP code of your residence to determine
      if sales tax needs to be handled.
    </Text>
    <Text my={2}>
      If you sign up for Raidbots Premium, we store API tokens from Stripe directly on Raidbots. Your credit card
      information is securely stored on Stripe&apos;s servers, not Raidbots. Raidbots <em>does</em> have access to this
      data - it is used to show you your current payment method as well as determine applicable taxes. These tokens are
      required for administrative purposes and may be stored for the duration that you maintain an account on the site.
    </Text>

    <Heading level={4} mb={1} mt={3}>Patreon</Heading>
    <Text my={2}>
      If you connect your Raidbots account and Patreon account, we store the read-only token from Patreon for the
      duration necessary to provide the Services to you and to maintain your account. This token is used to determine
      your pledge status, pledge level, and Discord identity if you have connected to Discord on Patreon.
    </Text>

    <Heading level={4} mb={1} mt={3}>Third-Party Analytics</Heading>
    <Text my={2}>
      We use a third-party vendor to track basic site traffic and usage information (pages visited, buttons clicked,
      etc) for the purpose of operating the site and developing new features. Our third-party analytics vendor does not
      process any personal data.
    </Text>


    <Heading level={2} mb={1} mt={3}>How We Use Your Information</Heading>
    <Text my={2}>
      We use the information we collect to run and improve the site and to provide customer support.
    </Text>

    <Text my={2}>
      We only share personal information with external parties to provide you with Raidbots service functionality and
      related administrative functions, such as:
    </Text>

    <ul>
      <li><Text>Sending transactional emails (password resets, etc)</Text></li>
      <li><Text>Processing payments</Text></li>
    </ul>

    <Text my={2}>
      We do not share or sell your personal information with third parties for commercial purposes.
    </Text>

    <Text my={2}>
      We may disclose or transfer your information in connection with the sale, merger, bankruptcy, sale of assets or
      reorganization of our company.
    </Text>


    <Heading level={2} mb={1} mt={3}>Security</Heading>
    <Text my={2}>
      Raidbots uses a variety of techniques and technologies to take reasonable steps to keep your information secure.
      All website access must occur using HTTPS, passwords are hashed using industry standard methods (currently
      bcrypt), and encryption is used for much of the data at rest.
    </Text>


    <Heading level={2} mb={1} mt={3}>Accessing, Changing, or Deleting Your Information</Heading>
    <Text my={2}>
      You can access or change your data or delete your account and personal data on the
      {' '}<RaidbotsAccountLink />.
    </Text>

    <Text my={2}>
      Deleted information may remain in backups for up to 90 days. Backups are only used for disaster recovery.
    </Text>


    <Heading level={2} mb={1} mt={3}>Information for European Union Users</Heading>

    <Text my={2}>
      In order to provide our Services to you, Raidbots collects, stores, and uses your information outside of the
      European Union (&quot;EU&quot;). For those users who reside in the EU, Switzerland, or the U.K., and who are
      subject to the General Data Protection Regulation, or are subject to either the data protection laws implemented
      by member states of the EU under the General Data Protection Regulation or to the UK Data Protection Act of 2018,
      as amended and incorporated into UK law (collectively, the &quot;GDPR&quot;), you may be entitled to exercise
      additional rights as a data subject and request additional information regarding Raidbots&apos;s data processing
      practices relating to your personal data. Under the GDPR, you are a &quot;Data Subject&quot; and we are a
      &quot;Data Controller&quot; for any personal data that constitutes &quot;personal data&quot; under the GDPR that
      is processed under this policy.
    </Text>

    <Text my={2}>
      If you are not a Data Subject, this section does not apply to you.
    </Text>

    <Text my={2}>
      <em>
        Lawful Grounds for Processing Personal Data of Data Subjects
      </em>
    </Text>


    <Text my={2}>
      We rely upon the following legal grounds to process personal data under the GDPR:
    </Text>


    <ul>
      <li>
        <Text>
          If you have consented to the processing of personal data for one or more specific purposes.
        </Text>
      </li>
      <li>
        <Text>
          If the processing is necessary for the performance of an agreement with you (including for any pre-contractual
          obligations thereof), for our compliance with a legal obligation to which we are subject, to protect your
          vital interests or of another natural person, or otherwise for our legitimate interests.
        </Text>
      </li>
    </ul>

    <Text my={2}>
      <em>
        Data Transfer Notice
      </em>
    </Text>


    <Text my={2}>
      We use servers located in the United States to process your personal data. By using our services, you acknowledge
      and agree that we are authorized to transfer your personal data for processing in the United States. Under the
      GDPR, the transfer of personal data to a country outside the EU may take place where the European Commission has
      determined that the country ensures an adequate level of protection. The transfer of your personal data to the
      United States in the absence of an adequacy decision by the European Commission is made because it is necessary
      for the performance of a contract with you, or with your explicit consent.
    </Text>


    <Text my={2}>
      Raidbots endeavors to process the minimum amount of personal data necessary to provide you with the Services that
      you have requested. As such, without your consent to process your information outside of the EU, some of our
      Services may not function correctly, or we may not be able to provide you with our Services at all. You may
      withdraw consent at anytime by deleting your account in the <RaidbotsAccountLink />, or by contacting us at the
      e-mail address listed under &quot;Individual Rights and Data Subject Requests&quot;.
    </Text>


    <Text my={2}>
      <em>
        Retention of personal data
      </em>
    </Text>


    <Text my={2}>
      We will keep your personal data as long as necessary to provide you with Services or to comply with applicable
      laws.  Pursuant to the GDPR, we will permanently erase your personal data at such time there is no lawful basis or
      legal obligation for us to store or process the personal data.
    </Text>


    <Text my={2}>
      You can delete your account and personal data by accessing your account on the <RaidbotsAccountLink />.
    </Text>


    <Text my={2}>
      <em>
        Individual Rights and Data Subject Requests
      </em>
    </Text>


    <Text my={2}>
      If you are a Data Subject and we process your personal data, you may exercise certain rights under the GDPR, which
      are described below. We will endeavor to respond to any requests within 30 days from receipt, but in some
      instances it may take longer. We will inform you within 30 days from receipt of your request if an extension is
      necessary and the reason for the delay.
    </Text>


    <Text my={2}>
      We provide you with tools on the <RaidbotsAccountLink /> to exercise your rights to access, transfer, rectify or
      erase your personal data.  If you have any questions or need to make an individual request, you can contact us at
      {' '}<PrivacyEmailLink />. Please provide specific and detailed information about your request necessary for us to
      respond to and carry out your request.
    </Text>


    <ul>
      <li>
        <Text>
          <strong>You may object to processing:</strong> You may object to certain types of processing, including
          processing for direct marketing (i.e. if you no longer want to be contacted with potential opportunities).
        </Text>
      </li>
      <li>
        <Text>
          <strong>You may request to be informed about our processing:</strong> You may request information about how we
          process your personal data and request additional information about how to exercise your rights under the
          GDPR.
        </Text>
      </li>
      <li>
        <Text>
          <strong>You may obtain access to your personal data:</strong> You may obtain access to your personal data that
          we process.
        </Text>
      </li>
      <li>
        <Text>
          <strong>You may request rectification:</strong> If you believe any personal data that we process is incorrect
          or incomplete, you may request that we correct it.
        </Text>
      </li>
      <li>
        <Text>
          <strong>You may request deletion of your personal data:</strong> Under certain conditions, you may request
          that we remove or delete your personal data. For example, if there is no legitimate reason for us to continue
          processing it. In accordance with applicable law, we may retain certain personal data to keep a record of our
          compliance with your request.
        </Text>
      </li>
      <li>
        <Text>
          <strong>You may restrict our processing of your personal data:</strong> You may request that we cease further
          processing of your personal data. If you make such a request, we may continue to store your personal data but
          will not make further use of it.
        </Text>
      </li>
      <li>
        <Text>
          <strong>You may withdraw consent:</strong> If we are relying on consent as the lawful grounds on which we
          process your personal data, you may withdraw your consent for such processing at any time without affecting
          the lawfulness of processing based upon your consent before it is withdrawn.
        </Text>
      </li>
    </ul>

    <Text my={2}>
      In addition to the rights granted by applicable laws, you may submit a complaint about our processing of your
      personal data to your national data protection regulator.
    </Text>


    <Heading level={2} mb={1} mt={3}>Children</Heading>
    <Text my={2}>
      Our Services are for users age 16 and over and we do not knowingly collect personal information from children
      under the age of 16. If you are a parent or guardian of a child under the age of 16 and believe he or she has
      disclosed personal information to us please contact us at <PrivacyEmailLink />.
    </Text>


    <Heading level={2} mb={1} mt={3}>Contact Us</Heading>
    <Text my={2}>
      If you have any questions regarding this privacy policy, please contact us at <PrivacyEmailLink />.
    </Text>


    <Heading level={2} mb={1} mt={3}>Privacy Policy Changes</Heading>
    <Text my={2}>
      All changes to the privacy policy will be listed here.
    </Text>

    <Text my={1}>
      <strong>October 10, 2023:</strong> Added more information for EU users, clarified retention durations for various
      categories, expanded the Privacy Policy to include email communications and the Raidbots Discord server,
      generalized Third-Party Analytics section
    </Text>
    <Text my={1}>
      <strong>June 6, 2023:</strong> Updated &quot;Third-Party Analytics&quot; to indicate the switch to Plausible.io
      from Google Analytics
    </Text>
    <Text my={1}>
      <strong>July 15, 2020:</strong> Updated &quot;Stripe / Raidbots Premium&quot; to add information collected about
      your residence.
    </Text>
    <Text my={1}>
      <strong>November 4, 2019:</strong> Updated &quot;Information We Collect&quot; to indicate 30 day retention policy
      for simulation data.
    </Text>
    <Text my={1}>
      <strong>July 14, 2018:</strong> Added information about Stripe and Raidbots Premium
    </Text>
    <Text my={1}>
      <strong>May 10, 2018:</strong> Initial version published
    </Text>

  </>
);

const PrivacyPolicyPage = () => (
  <>
    <PageHelmet page="privacy" />
    <Flex justify="center">
      <Box lg={8} sm={12}>
        <PrivacyPolicy />
      </Box>
    </Flex>
  </>
);

export default PrivacyPolicyPage;
